import { useEffect, useRef, useState } from "react";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Table } from "lucide-react";

const MAX_ROWS = 8;
const MAX_COLS = 8;

export function TableInsertButton() {
    const [editor] = useLexicalComposerContext();
    const [open, setOpen] = useState(false);
    const [hover, setHover] = useState<{ row: number; col: number }>({ row: 0, col: 0 });
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [open]);

    const selectSize = (rows: number, cols: number) => {
        editor.dispatchCommand(INSERT_TABLE_COMMAND, {
            rows: String(rows),
            columns: String(cols),
            includeHeaders: false,
        });
        setOpen(false);
        setHover({ row: 0, col: 0 });
    };

    return (
        <div className="rte-toolbar-button-wrapper" ref={wrapperRef}>
            <button
                type="button"
                className="rte-toolbar-button"
                data-active={open}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setOpen((v) => !v)}
                title="Insert table"
                aria-label="Insert table"
                aria-expanded={open}
            >
                <Table size={16} />
            </button>
            {open && (
                <div className="rte-table-picker" onMouseDown={(e) => e.preventDefault()}>
                    <div
                        className="rte-table-picker-grid"
                        onMouseLeave={() => setHover({ row: 0, col: 0 })}
                    >
                        {Array.from({ length: MAX_ROWS }).map((_, r) =>
                            Array.from({ length: MAX_COLS }).map((__, c) => (
                                <div
                                    key={`${r}-${c}`}
                                    className="rte-table-picker-cell"
                                    data-selected={r < hover.row && c < hover.col}
                                    onMouseEnter={() => setHover({ row: r + 1, col: c + 1 })}
                                    onClick={() => selectSize(hover.row, hover.col)}
                                />
                            )),
                        )}
                    </div>
                    <div className="rte-table-picker-label">
                        {hover.row > 0
                            ? `${hover.row} × ${hover.col}`
                            : "Pick table size"}
                    </div>
                </div>
            )}
        </div>
    );
}
